import React from 'react';
import { Link } from 'react-router-dom';
import { Crown, Newspaper, Music, Video, Film, Shield, Clock, MapPin, Users, Lock, Gamepad2, Handshake, BookOpen, Search } from 'lucide-react';
import SEO from '../../components/SEO';

const ExploreHub = () => {
  // Schema.org JSON-LD for Explore Page - Maximum SEO
  const exploreSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Explore Michael Jackson Legacy - PopCrown Fan Community",
    "description": "Explore the complete Michael Jackson legacy - timeline from 1958-2009, discography, music videos, Legal Vault with FBI files, world tour locations, and more. The ultimate King of Pop resource for MJ fans.",
    "url": "https://popcrownofficial.com/explore",
    "about": {
      "@type": "Person",
      "name": "Michael Jackson",
      "alternateName": ["King of Pop", "MJ"],
      "description": "American singer, songwriter, and dancer. One of the most significant cultural figures of the 20th century."
    },
    "hasPart": [
      {"@type": "WebPage", "name": "Timeline", "description": "Complete Michael Jackson history 1958-2009"},
      {"@type": "WebPage", "name": "Discography", "description": "Every MJ album from Jackson 5 to Invincible"},
      {"@type": "WebPage", "name": "Legal Vault", "description": "FBI files, court records, documented facts"},
      {"@type": "WebPage", "name": "World Tour", "description": "44+ iconic MJ locations worldwide"},
      {"@type": "WebPage", "name": "Music Videos", "description": "Thriller, Beat It, Billie Jean, Bad and more"}
    ]
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {"@type": "ListItem", "position": 1, "name": "Home", "item": "https://popcrownofficial.com"},
      {"@type": "ListItem", "position": 2, "name": "Explore", "item": "https://popcrownofficial.com/explore"}
    ]
  };

  // Members app subscription link
  const subscriptionJoinLink = 'https://members.popcrownofficial.com/subscription/join';

  const categories = [
    {
      icon: <Newspaper className="w-8 h-8" />,
      title: 'Latest News',
      description: 'News, updates, and announcements curated for the community.',
      path: '/explore/news',
      color: '#E10600',
      free: true
    },
    {
      icon: <Music className="w-8 h-8" />,
      title: 'Music',
      description: 'Browse releases via Spotify and other hosted platforms. We do not host audio.',
      path: '/explore/music',
      color: '#1DB954',
      free: true
    },
    {
      icon: <Video className="w-8 h-8" />,
      title: 'Videos',
      description: 'Watch via publicly available links on their respective platforms. We do not host video.',
      path: '/explore/videos',
      color: '#FF0000',
      free: true
    },
    {
      icon: <Film className="w-8 h-8" />,
      title: '2026 Movie',
      description: 'Updates and information about upcoming pop culture releases.',
      path: '/explore/movies',
      color: '#9333EA',
      free: true
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Legal Vault',
      description: 'Official FBI files, court records & legal documents.',
      path: '/explore/legal-vault',
      color: '#8B5CF6',
      free: true
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: 'Timeline',
      description: 'Journey through the complete history from 1958-2009.',
      path: '/explore/timeline',
      color: '#DC143C',
      free: false
    },
    {
      icon: <MapPin className="w-8 h-8" />,
      title: 'MJ World Tour Locations',
      description: 'Visit iconic Michael Jackson locations around the globe — from Neverland to Motown.',
      path: '/explore/tour-locations',
      color: '#06B6D4',
      free: false
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Michael Jackson Fan Community',
      description: 'Join a safe space for Michael Jackson fans to connect and share.',
      path: '/explore/community',
      color: '#EC4899',
      free: false
    }
  ];

  const valueProps = [
    { Icon: Handshake, title: 'Find Your People', description: 'Connect with Michael Jackson fans who get it. Your tribe is here.' },
    { Icon: Shield, title: 'Built by Fans, for Fans', description: 'The world\'s only dedicated Michael Jackson fan app. Built to celebrate the King of Pop\'s legacy.' },
    { Icon: BookOpen, title: 'Preserve What Matters', description: 'Michael Jackson\'s stories, music history, and legacy — preserved for future generations.' },
    { Icon: Search, title: 'Discover the Real Story', description: 'Court documents, FBI files, and verified timelines proving Michael Jackson\'s innocence.' }
  ];

  return (
    <div className="min-h-screen pt-20">
      <SEO
        title="Explore Michael Jackson — Timeline, Music, Legal Vault, News | PopCrown"
        description="Explore Michael Jackson's complete legacy. Timeline from Jackson 5 to This Is It, discography, music videos, Legal Vault with FBI files, world tour locations, and breaking MJ news."
        keywords="Michael Jackson timeline, MJ discography, Michael Jackson music videos, Michael Jackson Legal Vault, FBI files Michael Jackson, MJ world tour, Michael Jackson news, King of Pop history, Thriller, Bad, Dangerous, HIStory, Off the Wall, Jackson 5"
        canonical="/explore"
      />
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        {/* Background Image - Performer Silhouette - SEO Optimized */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?q=80&w=2070)',
          }}
          role="img"
          aria-label="Performer silhouette on stage representing Michael Jackson iconic performances - explore King of Pop history timeline legal vault and more"
        />
        {/* Hidden img for SEO */}
        <img 
          src="https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?q=80&w=2070"
          alt="Performer silhouette on concert stage with dramatic lighting representing Michael Jackson iconic performances - explore King of Pop MJ history timeline Jackson 5 origins Motown era Thriller album Bad era Dangerous HIStory legacy at PopCrown fan community"
          className="sr-only"
          loading="eager"
        />
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(135deg, rgba(220, 20, 60, 0.4) 0%, rgba(0, 0, 0, 0.85) 50%, rgba(225, 6, 0, 0.3) 100%)'
        }} />
        
        <div className="container mx-auto px-4 text-center relative z-10">
          <img src="/popcrown-crown-red.png" alt="" aria-hidden="true" className="w-16 h-16 mx-auto mb-6 crown-pulse object-contain" />
          <h1 className="text-5xl md:text-7xl font-bold mb-4">
            <span className="thriller-text">Explore</span>
          </h1>
          <p className="text-xl md:text-2xl text-white mb-4">
            Discover. Learn. Celebrate.
          </p>
          <p className="text-gray-300 max-w-2xl mx-auto mb-8">
            Your gateway to pop culture history. Free content for everyone, premium experiences for members.
          </p>
          <p className="text-xs text-gray-400">
            Built by fans, for fans. Not affiliated with any artist estates or rights holders.
          </p>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-12" style={{ background: 'linear-gradient(180deg, #1a1a1a 0%, #000 100%)' }}>
        <div className="container mx-auto px-4 text-center">
          <blockquote className="text-2xl md:text-3xl text-white mb-4" style={{
            fontFamily: "'Great Vibes', cursive",
            lineHeight: '1.6'
          }}>
            <span className="text-[#E10600]">"</span>
            In a world filled with hate, we must still dare to hope. In a world filled with anger, we must still dare to comfort.
            <span className="text-[#E10600]">"</span>
          </blockquote>
        </div>
      </section>

      {/* Categories Grid */}
      <section className="py-20" style={{ background: 'linear-gradient(180deg, #FFFFFF 0%, #FFF5F5 100%)' }}>
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">
              Start <span className="thriller-text">Exploring</span>
            </h2>
            <p className="text-gray-600">Choose a category to dive in</p>
            <div className="mx-auto mt-4 h-1 w-24 bg-gradient-to-r from-[#DC143C] via-[#E10600] to-[#DC143C]"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {categories.map((cat, index) => (
              <Link
                key={index}
                to={cat.path}
                className="group relative p-6 rounded-xl transition-all duration-300 hover:transform hover:scale-105"
                style={{
                  background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
                  border: `2px solid ${cat.color}40`,
                  boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
                }}
              >
                {!cat.free && (
                  <div className="absolute top-3 right-3">
                    <Lock className="w-4 h-4 text-[#E10600]" />
                  </div>
                )}
                <div style={{ color: cat.color }} className="mb-4 transition-transform group-hover:scale-110">
                  {cat.icon}
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{cat.title}</h3>
                <p className="text-gray-400 text-sm">{cat.description}</p>
                {!cat.free && (
                  <span className="inline-block mt-3 px-3 py-1 bg-[#E10600]/20 text-[#E10600] rounded-full text-xs font-semibold">
                    Preview Available
                  </span>
                )}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* What We Stand For */}
      <section className="py-20" style={{ background: 'linear-gradient(180deg, #FFF5F5 0%, #FFFFFF 100%)' }}>
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">
              What We <span className="thriller-text">Stand For</span>
            </h2>
            <p className="text-gray-600">A community built on belonging, safety, and truth</p>
            <div className="mx-auto mt-4 h-1 w-24 bg-gradient-to-r from-[#DC143C] via-[#E10600] to-[#DC143C]"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {valueProps.map((prop, index) => {
              const IconEl = prop.Icon;
              return (
                <div 
                  key={index}
                  className="p-6 rounded-xl text-center bg-white"
                  style={{
                    border: '1px solid rgba(225, 6, 0, 0.15)',
                    boxShadow: '0 6px 24px rgba(0,0,0,0.06)'
                  }}
                >
                  <div className="mb-4 flex justify-center">
                    <IconEl className="w-10 h-10 text-[#E10600]" strokeWidth={1.5} aria-hidden="true" />
                  </div>
                  <h3 className="text-lg font-bold text-black mb-2">{prop.title}</h3>
                  <p className="text-gray-600 text-sm">{prop.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Premium Teaser */}
      <section className="py-20" style={{ background: 'linear-gradient(180deg, #FFFFFF 0%, #FFF5F5 100%)' }}>
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">
              Unlock <span className="thriller-text">Premium</span>
            </h2>
            <p className="text-gray-600">Exclusive features for Gold Members</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-12">
            {[
              { Icon: Gamepad2, title: '10+ Interactive Games', desc: 'Quizzes, trivia, leaderboards' },
              { Icon: Users, title: 'Global Community', desc: 'Fans from 130+ countries' },
              { Icon: MapPin, title: 'World Tour Map', desc: '17+ iconic locations' }
            ].map((item, i) => {
              const IconEl = item.Icon;
              return (
                <div key={i} className="p-6 rounded-xl text-center relative overflow-hidden bg-white" style={{
                  border: '1px solid rgba(225, 6, 0, 0.2)',
                  boxShadow: '0 6px 24px rgba(0,0,0,0.06)'
                }}>
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <Lock className="w-16 h-16 text-[#E10600]/10" />
                  </div>
                  <div className="relative z-10">
                    <div className="mb-3 flex justify-center">
                      <IconEl className="w-9 h-9 text-[#E10600]" strokeWidth={1.5} aria-hidden="true" />
                    </div>
                    <h3 className="text-lg font-bold text-black mb-1">{item.title}</h3>
                    <p className="text-gray-600 text-sm">{item.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing CTA */}
      <section className="py-20" style={{ background: 'linear-gradient(180deg, #1a1a1a 0%, #000 100%)' }}>
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <img src="/popcrown-crown-red.png" alt="" aria-hidden="true" className="w-12 h-12 mx-auto mb-4 object-contain" />
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              The App is <span className="thriller-text">FREE</span>. The Conversation is <span className="thriller-text">Inside</span>.
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Daily quizzes, the Legal Vault, In His Words and the leaderboard — all free. Want to join the conversation, get early event tickets and exclusive discounts? That&apos;s the PopCrown Community.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto items-stretch">
            <div className="p-8 rounded-xl text-center flex flex-col" style={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)',
              border: '2px solid rgba(225, 6, 0, 0.3)'
            }}>
              <h3 className="text-2xl font-bold text-white mb-2">The App</h3>
              <div className="text-4xl font-bold thriller-text mb-1" style={{ lineHeight: 1.1 }}>FREE</div>
              <p className="text-sm text-gray-400 mb-4">Forever · iPhone &amp; Android</p>
              <a href="/app#install" className="btn-outline-gold w-full block text-center mt-auto" data-testid="explore-app-cta">Install Free on Your Phone</a>
            </div>
            <div className="p-8 rounded-xl text-center relative flex flex-col" style={{
              background: 'linear-gradient(135deg, rgba(225, 6, 0, 0.15) 0%, rgba(220, 20, 60, 0.1) 100%)',
              border: '2px solid #E10600'
            }}>
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 px-4 py-1 bg-[#DC143C] text-white text-xs font-bold rounded-full">
                JOIN THE CONVERSATION
              </div>
              <h3 className="text-2xl font-bold text-white mb-2 mt-2">PopCrown Community</h3>
              <div className="text-4xl font-bold text-[#E10600] mb-1">£1.99<span className="text-lg text-gray-400">/mo</span></div>
              <p className="text-sm text-gray-400 mb-4">Cancel anytime</p>
              <a href={subscriptionJoinLink} className="btn-gold w-full block text-center mt-auto" data-testid="explore-crown-cta">Join PopCrown Community</a>
            </div>
          </div>

          <p className="text-center text-gray-500 text-sm mt-8">
            Free forever · PopCrown Community cancel anytime · Ad-free experience
          </p>
        </div>
      </section>

      {/* Disclaimer Footer */}
      <section className="py-8 bg-black">
        <div className="container mx-auto px-4 text-center">
          <p className="text-gray-500 text-sm">
            PopCrown Official Ltd is an independent fan community. Not affiliated with, endorsed by, or connected to any artist estates or rights holders.
          </p>
        </div>
      </section>
    </div>
  );
};

export default ExploreHub;
